import { format } from "date-fns";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LOAD,
  LOAD_END,
  SELECT_DATE_DRIVERS,
} from "../../../../../../../store/actions/actionsTypes";
import { RootState } from "../../../../../../../store/reducers/rootReducer";
import { Defaults } from "../../../../../../modules/API/Defaults/defaults";
import { ITableOfTableItem } from "../../../../../../modules/API/Defaults/models/tableOfTables";
import { Trucks } from "../../../../../../modules/API/Distribution/trucks";
import { Drivers } from "../../../../../../modules/API/Drivers/drivers";
import { IKavDataItem } from "../../../../../../modules/API/Drivers/models/kavData";
import { ContextModal } from "../../../../../../modules/modal/modalContext";
import useDataFetcher from "../../../knisot/hooks/useFetchData";
import { filterKavs } from "../../helpers/driversFilter";

export interface IUseDriversData {
  data: IKavDataItem[] | null;
  setData: React.Dispatch<React.SetStateAction<IKavDataItem[] | null>>;
  renderList: IKavDataItem[];
  isLoading: boolean;
  date: string;
  setDate: (value: string) => void;
  handleDate: (e: object) => void;
  defaultDate: string | null;
  input: string;
  setInput: (value: string) => void;
  isOffice: boolean;
  refetch: () => Promise<void>;
  defaultStatuses: ITableOfTableItem[] | undefined;
  listPacakgesActions: ITableOfTableItem[] | undefined;
  listTeudotActions: ITableOfTableItem[] | undefined;
}

export const useDriversData = (): IUseDriversData => {
  const dispatch = useDispatch();
  const [, setModal] = useContext(ContextModal);
  const user = useSelector((state: RootState) => state.mainReducer.user);
  const defaultDate = useSelector((state: RootState) => state.driverDerucer.date);

  const isOffice = user
    ? user?.code_internal === "0" || user?.rights?.includes("TRCOMPANY")
    : false;

  // Дату нельзя оставлять пустой: страница кава открывается и напрямую по URL
  // (перезагрузка, переход по ссылке), а контрол даты живёт не только там.
  // Формат тот же, что ставит generic/ui/DatePicker на маунте.
  const [date, setDate] = useState<string>(() =>
    format(defaultDate ? new Date(defaultDate) : new Date(), "yyyyMMdd")
  );
  const [input, setInput] = useState<string>("");
  const [data, setData] = useState<IKavDataItem[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [defaultStatuses, setDefaultStatuses] = useState<ITableOfTableItem[]>();

  // Легаси-нотификация об ошибке: баннер на 3 секунды.
  const showError = useCallback(() => {
    setModal({ open: true, value: "משהו השתבש", mood: false });
    setTimeout(() => {
      setModal({ open: false, value: "2" });
    }, 3000);
  }, [setModal]);

  const refetch = useCallback(async () => {
    if (!date) return;
    dispatch({ type: LOAD });
    setIsLoading(true);
    try {
      if (isOffice) {
        const res: IKavDataItem[] = await Trucks.getOrdersTree(
          `${date}&ShowDeliveries=1`
        );
        // Первый элемент дерева — служебный, легаси отбрасывал его splice(0,1).
        setData(res.slice(1));
      } else {
        const res: IKavDataItem[] = await Drivers.getDriverOrders(date);
        setData(res);
      }
    } catch (err) {
      showError();
    } finally {
      setIsLoading(false);
      dispatch({ type: LOAD_END });
    }
  }, [date, dispatch, isOffice, showError]);

  const getDefaultStatuses = useCallback(async () => {
    try {
      const res = await Defaults.getStandartTableItems(112);
      setDefaultStatuses(res);
    } catch (err) {
      showError();
    }
  }, [showError]);

  useEffect(() => {
    if (!date) return;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    getDefaultStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: listPacakgesActions } = useDataFetcher<ITableOfTableItem[]>(
    Defaults.getStandartTableItems,
    "129"
  );

  const { data: listTeudotActions } = useDataFetcher<ITableOfTableItem[]>(
    Defaults.getStandartTableItems,
    "128"
  );

  const renderList = useMemo(
    () => (data ? filterKavs(data, input) : []),
    [data, input]
  );

  const handleDate = useCallback(
    (e: object) => {
      dispatch({ type: SELECT_DATE_DRIVERS, payload: e });
    },
    [dispatch]
  );

  return {
    data,
    setData,
    renderList,
    isLoading,
    date,
    setDate,
    handleDate,
    defaultDate,
    input,
    setInput,
    isOffice,
    refetch,
    defaultStatuses,
    listPacakgesActions: listPacakgesActions ?? undefined,
    listTeudotActions: listTeudotActions ?? undefined,
  };
};

export default useDriversData;
